import { Box3, Group, Matrix4, Mesh, Object3D, Vector3 } from 'three'

interface MeshPoint {
  center: Vector3
  box: Box3
}

/**
 * Transform from GLTF model space to scene space.
 * GltfModel (ui-toolkit) renders the model inside a group with rotation [Math.PI/2, 0, 0],
 * so we must apply the same transform for camera targets to match the visible geometry.
 * Rotation π/2 around X: (x, y, z) → (x, -z, y).
 */
function modelToScenePosition(x: number, y: number, z: number): [number, number, number] {
  return [x, -z, y]
}

function updateWorldMatricesIterative(root: Object3D): void {
  const ancestors: Object3D[] = []
  let ancestor = root.parent

  while (ancestor) {
    ancestors.push(ancestor)
    ancestor = ancestor.parent
  }

  for (let i = ancestors.length - 1; i >= 0; i--) {
    const object = ancestors[i]
    if (object.matrixAutoUpdate) object.updateMatrix()

    if (object.parent) {
      object.matrixWorld.multiplyMatrices(object.parent.matrixWorld, object.matrix)
    } else {
      object.matrixWorld.copy(object.matrix)
    }
  }

  const stack: Object3D[] = [root]
  while (stack.length > 0) {
    const object = stack.pop()!
    if (object.matrixAutoUpdate) object.updateMatrix()

    if (object.parent) {
      object.matrixWorld.multiplyMatrices(object.parent.matrixWorld, object.matrix)
    } else {
      object.matrixWorld.copy(object.matrix)
    }

    for (let i = object.children.length - 1; i >= 0; i--) {
      stack.push(object.children[i])
    }
  }
}

/**
 * Collect world-space bounding boxes and centers for meshes without using
 * recursive Three.js traversal helpers, which can overflow the call stack on
 * deeply nested or very large model hierarchies.
 */
function collectMeshPoints(group: Group): { points: MeshPoint[]; overallBox: Box3 } {
  updateWorldMatricesIterative(group)

  const points: MeshPoint[] = []
  const overallBox = new Box3().makeEmpty()
  const tempCenter = new Vector3()
  const stack: Object3D[] = [group]

  while (stack.length > 0) {
    const object = stack.pop()!

    if (object instanceof Mesh && object.geometry) {
      if (!object.geometry.boundingBox) {
        object.geometry.computeBoundingBox()
      }

      if (object.geometry.boundingBox) {
        const box = object.geometry.boundingBox.clone().applyMatrix4(object.matrixWorld)
        box.getCenter(tempCenter)
        points.push({ center: tempCenter.clone(), box })
        overallBox.union(box)
      }
    }

    for (let i = object.children.length - 1; i >= 0; i--) {
      stack.push(object.children[i])
    }
  }

  return { points, overallBox }
}

/**
 * Overall bounding box of all meshes in the model, converted to scene (Z-up) coordinates of the
 * raw (unaligned) model — the same space `alignment_config.referencePointIFCCS` is defined in
 * (the loaded GLTF group is not mounted in the scene, so its world matrices carry no alignment).
 */
export function getSceneBoundingBoxFromGltfModel(group: Group): Box3 | null {
  const { overallBox } = collectMeshPoints(group)
  if (overallBox.isEmpty()) return null
  // Same GLTF-to-scene transform as `modelToScenePosition` (rotation π/2 around X for Z-up).
  return overallBox.applyMatrix4(new Matrix4().makeRotationX(Math.PI / 2))
}

function findParent(parent: number[], i: number): number {
  let root = i
  while (parent[root] !== root) {
    root = parent[root]
  }

  while (parent[i] !== i) {
    const next = parent[i]
    parent[i] = root
    i = next
  }

  return root
}

function union(parent: number[], size: number[], i: number, j: number): void {
  const a = findParent(parent, i)
  const b = findParent(parent, j)

  if (a === b) return

  if (size[a] < size[b]) {
    parent[a] = b
    size[b] += size[a]
    return
  }

  parent[b] = a
  size[a] += size[b]
}

function distanceSq(a: Vector3, b: Vector3): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  const dz = a.z - b.z
  return dx * dx + dy * dy + dz * dz
}

/**
 * Cluster meshes by spatial proximity: elements whose bounding box centers
 * are within (thresholdFraction * modelDiagonal) of each other form one group.
 * Each cluster's target point is the center of the combined bounding box of
 * all meshes in that group (so the point lies where the elements actually are).
 * Returns cluster centers in scene (world) coordinates. When the group is
 * mounted under GltfModel (rotation π/2 around X for Z-up), setFromObject
 * already yields world positions in scene (Z-up) space.
 */
export default function getSpatialClustersFromGltfModel(
  group: Group,
  thresholdFraction = 0.2,
): [number, number, number][] {
  const { points, overallBox } = collectMeshPoints(group)
  if (points.length === 0) return []

  const size = overallBox.getSize(new Vector3())
  const diagonal = size.length()
  const thresholdSq = (diagonal * thresholdFraction) ** 2

  const n = points.length
  const parent = Array.from({ length: n }, (_, i) => i)
  const sizeByRoot = Array.from({ length: n }, () => 1)

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (distanceSq(points[i].center, points[j].center) <= thresholdSq) {
        union(parent, sizeByRoot, i, j)
      }
    }
  }

  const rootToIndices = new Map<number, number[]>()
  for (let i = 0; i < n; i++) {
    const root = findParent(parent, i)
    if (!rootToIndices.has(root)) rootToIndices.set(root, [])
    rootToIndices.get(root)!.push(i)
  }

  const clusters: [number, number, number][] = []
  const clusterBox = new Box3()
  const clusterCenter = new Vector3()
  for (const indices of rootToIndices.values()) {
    clusterBox.makeEmpty()
    for (const i of indices) {
      clusterBox.union(points[i].box)
    }
    clusterBox.getCenter(clusterCenter)
    clusters.push(modelToScenePosition(clusterCenter.x, clusterCenter.y, clusterCenter.z))
  }

  return clusters
}
